import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from '../../store';

/**
 * Location Search Bar with Nominatim search
 * Flow: Tap location → X appears → Tap X → Search mode
 */
export function LocationSearchBar() {
  const [mode, setMode] = useState('display'); // 'display' | 'showX' | 'search'
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceTimer = useRef(null);
  
  const { 
    locationName, 
    isLoadingCurrent, 
    isCurrentLocation,
    isDefaultLocation,
    hasCurrentLocation,
    locationError,
    setCurrentLocationAsSelected,
    selectLocation,
    retryGPS
  } = useLocation();

  // Focus input when search mode is activated
  useEffect(() => {
    if (mode === 'search' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  // Fetch predictions using Nominatim when search query changes
  const fetchPredictions = useCallback(async (query) => {
    if (!query.trim() || query.trim().length < 2) {
      setPredictions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=in&format=json&limit=6&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (res.ok) {
        const data = await res.json();
        const results = data.map(item => {
          const addr = item.address || {};
          const mainText = addr.city || addr.town || addr.village || addr.state_district || item.display_name.split(',')[0];
          const secondaryText = addr.state || addr.country || '';
          return {
            id: item.place_id,
            mainText,
            secondaryText,
            fullAddress: item.display_name,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon),
          };
        });
        // Deduplicate by mainText
        const seen = new Set();
        const unique = results.filter(r => {
          const key = r.mainText.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setPredictions(unique.slice(0, 5));
      } else {
        setPredictions([]);
      }
    } catch (err) {
      console.warn('Search failed:', err);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!searchQuery.trim()) {
      setPredictions([]);
      return;
    }
    debounceTimer.current = setTimeout(() => {
      fetchPredictions(searchQuery);
    }, 400);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [searchQuery, fetchPredictions]);

  // Handle selecting a prediction
  const handleSelectPrediction = (prediction) => {
    selectLocation({
      name: prediction.mainText,
      fullAddress: prediction.fullAddress,
      coords: { lat: prediction.lat, lng: prediction.lng },
      type: 'selected'
    });
    setMode('display');
    setSearchQuery('');
    setPredictions([]);
  };

  // Handle tapping on location (show X)
  const handleTapLocation = () => {
    setMode('showX');
  };

  // Handle clicking X (enter search mode)
  const handleClickX = () => {
    setMode('search');
  };

  // Handle closing/canceling
  const handleClose = () => {
    setMode('display');
    setSearchQuery('');
    setPredictions([]);
  };

  // Render SEARCH mode - full screen overlay
  if (mode === 'search') {
    return (
      <>
        {/* Full screen overlay for search */}
        <div 
          className="fixed inset-0 bg-white z-50"
          style={{ top: 0 }}
        >
          {/* Search header */}
          <div className="bg-white p-4 border-b flex items-center gap-3">
            <button 
              onClick={handleClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#000000', padding: '4px' }}
              data-testid="location-search-back"
            >
              &lt;
            </button>
            <div className="flex-1 flex items-center gap-2 bg-gray-100 rounded-xl px-4 py-3">
              <span className="text-gray-400">🔍</span>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter location..."
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="location-search-input"
              />
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
              ) : searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  style={{
                    width: '20px',
                    height: '20px',
                    minWidth: '20px',
                    minHeight: '20px',
                    backgroundColor: '#000000',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ffffff',
                    fontSize: '11px',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* Search results */}
          <div className="px-4 pt-4">
            {/* Current Location - Always first option, plain text style */}
            <button
              onClick={() => {
                setCurrentLocationAsSelected();
                handleClose();
              }}
              className="w-full flex items-center gap-3 py-3 text-left"
              style={{ 
                background: 'transparent', 
                border: 'none',
                cursor: 'pointer'
              }}
              data-testid="select-current-location"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path 
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
                  fill="#4285F4"
                />
                <circle cx="12" cy="9" r="2.5" fill="#ffffff"/>
              </svg>
              <span className="text-gray-900">Current Location</span>
            </button>
            
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
              </div>
            )}
            
            {/* Predictions list */}
            {!isLoading && predictions.length > 0 && (
              <div>
                {predictions.map((prediction) => (
                  <button
                    key={prediction.id}
                    onClick={() => handleSelectPrediction(prediction)}
                    className="w-full flex items-center gap-3 py-3 text-left"
                    style={{ 
                      background: 'transparent', 
                      border: 'none',
                      cursor: 'pointer'
                    }}
                    data-testid={`prediction-${prediction.id}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                      <path 
                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
                        fill="#4285F4"
                      />
                      <circle cx="12" cy="9" r="2.5" fill="#ffffff"/>
                    </svg>
                    <span className="text-gray-900">
                      {prediction.mainText}
                      {prediction.secondaryText && (
                        <span className="text-gray-500">, {prediction.secondaryText}</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            {/* No results */}
            {!isLoading && searchQuery && predictions.length === 0 && (
              <div className="py-8">
                <p className="text-gray-500">No locations found</p>
              </div>
            )}
            
            {/* Empty state hint - before typing */}
            {!isLoading && !searchQuery && (
              <div className="py-4">
                <p className="text-gray-400 text-sm">Type to search other locations</p>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Render SHOW X mode (location with X button visible)
  if (mode === 'showX') {
    return (
      <div className="flex items-center gap-2">
        <span className="text-orange-500">📍</span>
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-semibold text-sm" data-testid="location-display-name"
               style={{ color: '#FFFFFF' }}>
              {isLoadingCurrent ? 'Detecting...' : locationName}
            </p>
            <button
              onClick={handleClickX}
              data-testid="location-x-btn"
              title="Change location"
              style={{
                width: '16px',
                height: '16px',
                minWidth: '16px',
                minHeight: '16px',
                backgroundColor: 'rgba(212, 175, 55, 0.3)',
                border: 'none',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#D4AF37',
                fontSize: '10px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {isCurrentLocation ? 'Current Location' : 'Selected'}
          </p>
        </div>
      </div>
    );
  }

  // Render DISPLAY mode (just location name, tap to show X)
  return (
    <div 
      className="flex items-center gap-2 cursor-pointer"
      onClick={handleTapLocation}
      data-testid="location-display"
    >
      <span className="text-orange-500">📍</span>
      <div className="min-w-0">
        <p className="font-semibold text-sm truncate" data-testid="location-display-name"
           style={{ color: '#FFFFFF' }}>
          {isLoadingCurrent ? 'Detecting...' : locationName}
        </p>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
          {isLoadingCurrent ? 'Getting GPS...' : isCurrentLocation ? 'Current Location' : 'Selected'}
        </p>
      </div>
    </div>
  );
}

export default LocationSearchBar;
